import { Button, Checkbox, Input as DSInput, Switch, SettingsSection, SelectField } from '@/design-system';
import {
  Save, Eye, EyeOff, AlertCircle, CreditCard, Receipt, Route, ChevronDown, ChevronRight,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { type BillingSettingsData, type GatewayProvider } from '../../api/billing-settings.service';

import { useBillingSettings } from '../../hooks/useBillingSettings';

function SectionField({ label, description, children }: { label: string; description?: React.ReactNode; children: React.ReactNode }) {
  return <div className="space-y-2 py-4">
    {label === 'Ambiente' && <p className="text-sm font-medium text-gray-900">{label}</p>}
    {children}
    {description && <p className="text-xs text-gray-500">{description}</p>}
  </div>;
}

function TextInput({ label, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { label?: string }) {
  return (
    <div>
      <DSInput
        {...props}
        label={label}
        className="text-sm disabled:text-gray-400"
      />
    </div>
  );
}

function SecretInput({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [show, setShow] = useState(false);
  const masked = value === '••••••••••';
  return (
    <div>
      <div className="relative">
        <DSInput
          type={show && !masked ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          label={label}
          className="pr-10 text-sm"
        />
        <Button type="button" variant="ghost" size="sm" aria-label={show ? 'Ocultar chave' : 'Mostrar chave'} onClick={() => setShow((v) => !v)}
          className="absolute right-1 bottom-1 h-auto px-1 text-gray-400 hover:text-gray-600">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </Button>
      </div>
      {masked && <p className="text-xs text-gray-500 mt-1">Chave configurada. Deixe como está para preservar; informe uma nova chave para substituí-la.</p>}
    </div>
  );
}

function Toggle({ enabled, onChange, label }: { enabled: boolean; onChange: (v: boolean) => void; label: string }) {
  return <Switch label={label} checked={enabled} onChange={onChange} />;
}

/**
 * Um provider é considerado "configurado" quando temos evidência no GET de que
 * as credenciais essenciais existem (chave mascarada ou preenchida localmente).
 */
function isProviderConfigured(provider: GatewayProvider, data: Partial<BillingSettingsData>): boolean {
  if (provider === 'STRIPE') {
    return Boolean(data.stripeApiKey) && Boolean(data.stripePublishableKey);
  }
  if (provider === 'ASAAS') {
    return Boolean(data.asaasApiKey);
  }
  return false;
}

function AsaasSection({
  data, onChange,
}: { data: Partial<BillingSettingsData>; onChange: (patch: Partial<BillingSettingsData>) => void }) {
  const [nfOpen, setNfOpen] = useState(!!data.nfEnabled);
  const isSandbox = (data.asaasBaseUrl || '').includes('sandbox');

  return (
    <div className="space-y-0">
      <SectionField label="API Key" description={
        <>Acesse <a href="https://www.asaas.com/apiKey" target="_blank" rel="noopener noreferrer" className="text-primary underline">asaas.com/apiKey</a> para obter sua chave.</>
      }>
        <SecretInput label="Chave de API" value={data.asaasApiKey ?? ''} onChange={(v) => onChange({ asaasApiKey: v })} placeholder="$aact_..." />
      </SectionField>

      <SectionField label="Ambiente" description="Sandbox para testes; Produção para cobranças reais.">
        <div className="flex gap-3">
          {[
            { label: 'Sandbox', url: 'https://sandbox.asaas.com/api/v3' },
            { label: 'Produção', url: 'https://api.asaas.com/v3' },
          ].map((env) => {
            const active = (data.asaasBaseUrl || 'https://api.asaas.com/v3') === env.url;
            return (
              <Button key={env.label} type="button" variant={active ? 'primary' : 'outline'} size="sm" onClick={() => onChange({ asaasBaseUrl: env.url })}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium border transition-colors ${
                  active ? 'bg-primary text-white border-primary' : 'text-gray-700 border-gray-300 hover:border-primary hover:text-primary'
                }`}>
                {env.label}
              </Button>
            );
          })}
        </div>
        {isSandbox && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <AlertCircle size={12} /> Modo sandbox ativo — cobranças não são reais.
          </p>
        )}
      </SectionField>

      <div className="py-4 border-b border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" onClick={() => setNfOpen((v) => !v)}
            aria-expanded={nfOpen}
            className="h-auto justify-start text-sm font-medium text-gray-900">
          <span className="flex items-center gap-2">
            {nfOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Nota Fiscal de Serviço (NFS-e)
          </span>
          </Button>
          <Toggle enabled={data.nfEnabled ?? false} onChange={(v) => { onChange({ nfEnabled: v }); if (v) setNfOpen(true); }} label={data.nfEnabled ? 'Ativo' : 'Inativo'} />
        </div>
        <p className="text-xs text-gray-500 ml-6 mt-0.5">Emissão automática ao confirmar pagamento.</p>

        {nfOpen && (
          <div className={`mt-4 ml-6 space-y-4 ${!data.nfEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="grid grid-cols-2 gap-3">
              <TextInput label="ID do Serviço Municipal" value={data.nfMunicipalServiceId ?? ''}
                onChange={(e) => onChange({ nfMunicipalServiceId: (e.target as HTMLInputElement).value })} placeholder="ex: 4" />
              <TextInput label="Código do Serviço (opcional)" value={data.nfMunicipalServiceCode ?? ''}
                onChange={(e) => onChange({ nfMunicipalServiceCode: (e.target as HTMLInputElement).value })} placeholder="ex: 1.01" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-600 mb-2">Alíquotas de Impostos (%)</p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'nfIssRate', label: 'ISS' }, { key: 'nfPis', label: 'PIS' }, { key: 'nfCofins', label: 'COFINS' },
                  { key: 'nfCsll', label: 'CSLL' }, { key: 'nfInss', label: 'INSS' }, { key: 'nfIr', label: 'IR' },
                ].map(({ key, label }) => (
                  <TextInput key={key} label={`${label} (%)`} type="number" step="0.01" min="0"
                    value={(data as Record<string, string>)[key] ?? '0'}
                    onChange={(e) => onChange({ [key]: (e.target as HTMLInputElement).value })} />
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox label="Reter ISS (tomador retém o imposto)" id="retainIss" checked={data.nfRetainIss ?? false}
                  onChange={(e) => onChange({ nfRetainIss: e.target.checked })} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StripeSection({
  data, onChange,
}: { data: Partial<BillingSettingsData>; onChange: (patch: Partial<BillingSettingsData>) => void }) {
  return (
    <div className="space-y-0">
      <SectionField label="Secret API Key" description={
        <>Chave secreta (<code className="text-[11px] bg-gray-100 px-1 rounded">sk_live_*</code> ou <code className="text-[11px] bg-gray-100 px-1 rounded">sk_test_*</code>). Copie em <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary underline">dashboard.stripe.com/apikeys</a>.</>
      }>
        <SecretInput label="Chave secreta (Secret Key)" value={data.stripeApiKey ?? ''} onChange={(v) => onChange({ stripeApiKey: v })} placeholder="sk_live_..." />
      </SectionField>

      <SectionField label="Webhook Signing Secret" description={
        <>Assinatura HMAC do endpoint de webhooks (<code className="text-[11px] bg-gray-100 px-1 rounded">whsec_*</code>). Configure o endpoint em <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-primary underline">dashboard.stripe.com/webhooks</a>.</>
      }>
        <SecretInput label="Segredo de assinatura do webhook" value={data.stripeWebhookSecret ?? ''} onChange={(v) => onChange({ stripeWebhookSecret: v })} placeholder="whsec_..." />
      </SectionField>

      <SectionField label="Publishable Key" description={
        <>Chave pública (<code className="text-[11px] bg-gray-100 px-1 rounded">pk_*</code>) enviada ao frontend para inicializar o Stripe.js.</>
      }>
        <TextInput label="Chave pública (Publishable Key)" value={data.stripePublishableKey ?? ''} placeholder="pk_live_..."
          onChange={(e) => onChange({ stripePublishableKey: (e.target as HTMLInputElement).value })} />
      </SectionField>
    </div>
  );
}

/**
 * Registro central dos providers suportados hoje. Adicionar um 3º (p.ex. BTG)
 * é: (1) incluir o id aqui, (2) expor seus campos editáveis num `<Section>`,
 * (3) cobrir em `isProviderConfigured` e (4) o backend aceitar o novo valor no
 * enum `GatewayProvider`.
 */
const PROVIDERS: {
  id: GatewayProvider;
  label: string;
  description: string;
  Section: (p: { data: Partial<BillingSettingsData>; onChange: (patch: Partial<BillingSettingsData>) => void }) => JSX.Element;
}[] = [
  { id: 'ASAAS', label: 'Asaas', description: 'PIX e Boleto (BR)', Section: AsaasSection },
  { id: 'STRIPE', label: 'Stripe', description: 'Cartão de crédito (multi-moeda)', Section: StripeSection },
];

const METHODS: { key: 'cardGateway' | 'pixGateway' | 'boletoGateway'; label: string; description: string }[] = [
  { key: 'cardGateway', label: 'Cartão de crédito', description: 'Usado em signup, retry de cobrança e troca de cartão.' },
  { key: 'pixGateway', label: 'PIX', description: 'Cobrança única ou recorrente via QR Code / copia-e-cola.' },
  { key: 'boletoGateway', label: 'Boleto', description: 'Cobrança via boleto bancário.' },
];

function MethodRoutingCard({ data, onChange }: { data: Partial<BillingSettingsData>; onChange: (patch: Partial<BillingSettingsData>) => void }) {
  return <SettingsSection title="Roteamento por método de pagamento" icon={Route} description="Selecione o gateway responsável por cada método de pagamento.">
    <div className="grid gap-4 pt-4 md:grid-cols-3">
      {METHODS.map((method) => {
        const current = data[method.key] ?? 'ASAAS';
        return <SelectField key={method.key} label={method.label} hint={method.description} value={current}
          onChange={(value) => onChange({ [method.key]: value as GatewayProvider })}
          options={PROVIDERS.map((provider) => ({
            value: provider.id, label: provider.label,
            disabled: (provider.id === 'ASAAS' ? data.asaasEnabled === false : data.stripeEnabled === false) || (!isProviderConfigured(provider.id, data) && current !== provider.id),
          }))} />;
      })}
    </div>
  </SettingsSection>;
}

export function GatewaysSection() {
  const { query, save } = useBillingSettings();
  const [draft, setDraft] = useState<Partial<BillingSettingsData>>({});
  if (query.isPending) return <p role="status" className="text-sm text-gray-500">Carregando gateways…</p>;
  if (!query.data) return <div role="alert" className="space-y-3 text-sm text-red-700">Não foi possível carregar os gateways.
    <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button></div>;
  const data = { ...query.data, ...draft };
  const enabled = (id: GatewayProvider) => id === 'ASAAS' ? data.asaasEnabled !== false : data.stripeEnabled !== false;
  const patch = (partial: Partial<BillingSettingsData>) => { setDraft((prev) => ({ ...prev, ...partial })); save.reset(); };
  return <div className="space-y-5">
    {query.isError && <p role="alert" className="text-sm text-red-700">Não foi possível atualizar os gateways. Os dados anteriores foram preservados.</p>}
    {save.isError && <p role="alert" className="text-sm text-red-700">Não foi possível salvar os gateways. Tente novamente.</p>}
    {save.isSuccess && <p role="status" className="text-sm text-emerald-700">Configurações salvas.</p>}
    <fieldset disabled={save.isPending} className="min-w-0 space-y-5">
      <MethodRoutingCard data={data} onChange={patch} />
      <SettingsSection title="Pagamento manual" icon={Wallet} description="Receba fora dos gateways e registre os pagamentos nas faturas."
        headerAside={<div className="flex items-center gap-3"><span className={data.manualEnabled !== false ? 'rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700' : 'rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-500'}>{data.manualEnabled !== false ? 'Ativo' : 'Inativo'}</span><Switch label="Ativar pagamento manual" hideLabel checked={data.manualEnabled !== false} onChange={(value) => patch({ manualEnabled: value })} /></div>}>
        <p className="pt-4 text-sm text-gray-500">Disponível no cadastro da empresa. O financeiro registra a baixa após confirmar o recebimento.</p>
      </SettingsSection>
      {PROVIDERS.map((provider) => <SettingsSection key={provider.id} title={provider.label}
        icon={provider.id === 'ASAAS' ? Receipt : CreditCard} description={provider.description}
        headerAside={
          <div className="flex items-center gap-3">
          <span className={isProviderConfigured(provider.id, query.data) ? 'rounded-md bg-emerald-50 px-2 py-1 text-xs text-emerald-700' : 'rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-700'}>
            {!enabled(provider.id) ? 'Inativo' : isProviderConfigured(provider.id, query.data) ? 'Configurado' : 'Credenciais pendentes'}
          </span>
          <Switch label={`Ativar ${provider.label}`} hideLabel checked={enabled(provider.id)} onChange={(value) => patch(provider.id === 'ASAAS' ? { asaasEnabled: value } : { stripeEnabled: value })} />
          </div>
        }>
        <provider.Section data={data} onChange={patch} />
      </SettingsSection>)}
    </fieldset>
    <p className="text-xs text-gray-500">Desativar impede novas contratações. As credenciais, faturas e contratos existentes são preservados.</p>
    <div className="flex justify-end">
      <Button isLoading={save.isPending} disabled={!Object.keys(draft).length}
        onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}><Save size={16} />Salvar configurações</Button>
    </div>
  </div>;
}
